import { getPlayer } from "@/api/privateRequests/players/getPlayer";
import { FloatingRailSlot } from "@/components/molecules/FloatingRailSlot/FloatingRailSlot";
import { PlayersToolbar } from "@/components/organisms/PlayersToolbar/PlayersToolbar";
import { rootCanonicalUrl } from "@/config/canonical";
import { GAME_NAME, PINNED_ASSET_SLUG } from "@/config/game";
import { headingVersionName } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { Button as DesignSystemButton } from "@/design-system/Button/Button";
import { useGoBack } from "@/hooks/useGoBack";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useSticky } from "@/hooks/useSticky";
import {
  PlayerNotFound,
  isNotFoundError,
} from "@/pages/DetailedPlayer/PlayerNotFound";
import { CardBuilderTopSection } from "@/pages/EvolutionsHub/EvolutionsBuilder/CardBuilderTopSection";
import { ToolbarAction } from "@/pages/EvolutionsHub/EvolutionsPage/EvolutionsPage";
import { EvolutionsStickySidebar } from "@/pages/EvolutionsHub/sections/EvolutionsStickySidebar";
import { getCurrentEvolutionPlayerSelector } from "@/redux/evolutions/evolutionsBuilderSlice";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import {
  PlayStylesLabRecommendation,
  addPlayerToPlayStylesLab,
  applyPlayStyle,
  applyRecommendation,
  clearPlayStylesLab,
  fetchPlayStylesLab,
  getCurrentPlayStylesLabPlayerSelector,
  playStylesLabAppliedSelector,
  playStylesLabRecommendationsSelector,
  playStylesLabSelector,
  playStylesLabStepsSelector,
  playStylesLabUpgradesSelector,
  resetPlayStyles,
  setActivePlayerRole,
} from "@/redux/playStylesLab/playStylesLabSlice";
import {
  PlayerRole,
  generateNavigationUrl,
  playersSelector,
  versionNameBySlug,
  versionUrlSlug,
} from "@/redux/players/playersSlice";
import { addPlayerOrChangeRole } from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { evolutionPathFromParam } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { saveEvolution } from "@repo/shared/redux";
import { capitalizeWordsAndRemoveDashes } from "@repo/shared/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { PlayStylesLabSteps } from "./components/PlayStylesLabSteps";
import { PlayStylesRecommendations } from "./components/PlayStylesRecommendations";

export const PlayStylesLab = () => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const [searchTerm, setSearchTerm] = useState("");
  const [elementOpened, setElementOpened] = useState<ToolbarAction>("none");
  const refPlayerRole = useOutsideClick(() => {
    if (elementOpened === "playerRole") setElementOpened("none");
  });
  const topToPage = useSelector(topToPageSelector);
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
    offset: isTouchDevice ? 0 + topToPage.value : -76 + topToPage.value,
  }) as [boolean, React.RefObject<HTMLDivElement>];

  const { name, version, resourceId } = useParams();
  // Keyed on the id so browsing on to another player clears it.
  const [notFoundResourceId, setNotFoundResourceId] = useState<string>();
  const [searchParams, setSearchParams] = useSearchParams();
  const evoPathId = searchParams.get("evo-path-id");
  const urlEvolutionPath = evolutionPathFromParam(
    searchParams.get("evolution-path"),
  );
  const evolutionPath = urlEvolutionPath?.evolutionPath;
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("players");
  const navigate = useNavigate();

  // Check if we came from squad builder via query param
  const comingFromSquadBuilder = useMemo(() => {
    return searchParams.get("from") === "squad-builder";
  }, []);

  const { basePlayer } = useSelector(playStylesLabSelector);
  const currentPlayStylesLabPlayer = useSelector(
    getCurrentPlayStylesLabPlayerSelector,
  );
  const evolutionBuilderPlayer = useSelector(getCurrentEvolutionPlayerSelector);

  const { recommendations } = useSelector(playStylesLabRecommendationsSelector);
  const { playStylesPlus, playStyles, systemChanged, status } = useSelector(
    playStylesLabStepsSelector,
  );
  const { labPlayStyles, labPlayStylesPlus, activePlayerRoleId } = useSelector(
    playStylesLabAppliedSelector,
  );
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const [awaitingLogin, setAwaitingLogin] = useState<"save" | undefined>();
  const [saving, setSaving] = useState(false);
  const canSave = useMemo(
    () => labPlayStyles?.length > 0 || labPlayStylesPlus?.length > 0,
    [labPlayStyles.length, labPlayStylesPlus.length],
  );

  const versionAssets = useSelector(getVersionAssetsSelector);
  const upgrades = useSelector(playStylesLabUpgradesSelector);
  const { playerRoles } = useSelector(playersSelector);

  const selectedPlayerRole = useMemo(
    () => playerRoles.find((r) => r.id === activePlayerRoleId),
    [playerRoles, activePlayerRoleId],
  );

  const handleDownloadAssets = () => {
    const versionSlug =
      version ||
      versionUrlSlug(versionAssets[currentPlayStylesLabPlayer?.versionId]);
    let queryParams = new URLSearchParams();
    if (currentPlayStylesLabPlayer?.evoPathId) {
      queryParams.set(
        "evo-path-id",
        currentPlayStylesLabPlayer.evoPathId?.toString(),
      );
    }
    queryParams.set("is-playstyles-lab", "true");
    if (labPlayStyles.length > 0) {
      queryParams.set("lab-playstyles", labPlayStyles.join(","));
    }
    if (labPlayStylesPlus.length > 0) {
      queryParams.set("lab-playstyles-plus", labPlayStylesPlus.join(","));
    }
    const queryString = queryParams.toString();
    navigate(
      `/players/${currentPlayStylesLabPlayer?.name}/${versionSlug}/${currentPlayStylesLabPlayer?.resourceId}/images${queryString ? `?${queryString}` : ""}`,
    );
  };

  const handleToolbarAction = (action: ToolbarAction) => {
    if (action === "saveEvolution") {
      handleSaveClicked();
      return;
    }
    setElementOpened(elementOpened === action ? "none" : action);
    if (action === "downloadAssets") {
      handleDownloadAssets();
    }
  };

  const handleSelectPlayerRole = (role: PlayerRole) => {
    dispatch(setActivePlayerRole(role ? role.id : undefined));
    if (isTouchDevice) setElementOpened("none");
  };

  const handleClearPlaystyles = () => {
    dispatch(resetPlayStyles());
  };

  useEffect(() => {
    return () => {
      dispatch(clearPlayStylesLab());
    };
  }, []);

  useEffect(() => {
    if (basePlayer) {
      dispatch(
        fetchPlayStylesLab({
          evoPathId: evoPathId ? evoPathId : undefined,
          evolutionPath,
          pathwayPath: urlEvolutionPath?.pathwayPath,
        }),
      );
    }
  }, [basePlayer?.resourceId, labPlayStyles, labPlayStylesPlus]);

  useEffect(() => {
    if (!basePlayer) return;
    const params = new URLSearchParams(searchParams);
    if (labPlayStyles.length > 0) {
      params.set("lab-playstyles", labPlayStyles.join(","));
    } else {
      params.delete("lab-playstyles");
    }
    if (labPlayStylesPlus.length > 0) {
      params.set("lab-playstyles-plus", labPlayStylesPlus.join(","));
    } else {
      params.delete("lab-playstyles-plus");
    }
    setSearchParams(params, { replace: true, preventScrollReset: true });
  }, [labPlayStyles, labPlayStylesPlus, basePlayer]);

  useEffect(() => {
    if (!systemChanged) {
      dispatch(
        fetchPlayStylesLab({
          evoPathId: evoPathId ? evoPathId : undefined,
          evolutionPath,
          pathwayPath: urlEvolutionPath?.pathwayPath,
        }),
      );
    }
  }, [activePlayerRoleId]);

  useEffect(() => {
    const fetchPlayer = async () => {
      const playerRoleId = searchParams.get("player-role-id");
      const player = await getPlayer({
        resourceId: Number(resourceId),
        playerRoleId: playerRoleId ? +playerRoleId : undefined,
        backendPlayerType: "base",
      }).catch((error) => {
        if (isNotFoundError(error)) setNotFoundResourceId(resourceId);
        throw error;
      });

      const parseIds = (value: string | null) =>
        value
          ? value
              .split(",")
              .map(Number)
              .filter((n) => !Number.isNaN(n))
          : [];

      dispatch(
        addPlayerToPlayStylesLab({
          player: {
            ...player,
            resourceId: Number(resourceId),
            playerRoleId: playerRoleId ? +playerRoleId : undefined,
          },
          labPlayStyles: parseIds(searchParams.get("lab-playstyles")),
          labPlayStylesPlus: parseIds(searchParams.get("lab-playstyles-plus")),
          playerRoleId: playerRoleId ? +playerRoleId : undefined,
        }),
      );
    };

    // Keyed on which player the URL points at, not merely "is one loaded" —
    // otherwise back/forward between two lab URLs keeps the stale card.
    if (resourceId && String(basePlayer?.resourceId) !== resourceId) {
      fetchPlayer();
    }
  }, [resourceId, basePlayer]);

  // The lab URL is shareable, so deep-linked visitors fall back to the picker
  // this lab is rendered over.
  const goBack = useGoBack();

  const sendToSquadBuilder = () => {
    // Prefer the player the squad builder handed over (its metaRatings carry
    // the squad's chemistry/chemstyle entries); the lab's own copy only has
    // the lab response's ratings and shows the wrong value on the pitch.
    const player =
      evolutionBuilderPlayer?.resourceId ===
      currentPlayStylesLabPlayer?.resourceId
        ? evolutionBuilderPlayer
        : currentPlayStylesLabPlayer;
    if (!player) return;
    dispatch(
      addPlayerOrChangeRole({
        player: {
          ...player,
          evolutionPath: evolutionPath ?? player.evolutionPath ?? [],
          pathwayPath: urlEvolutionPath?.pathwayPath ?? player.pathwayPath,
        },
      }),
    );
    // A destination, not a back step: this action exists to hand the player to
    // the squad builder, whether or not the visitor arrived from there.
    navigate("/squad-builder");
  };

  const handleApplyRecommendation = (
    recommendation: PlayStylesLabRecommendation,
  ) => {
    dispatch(applyRecommendation(recommendation));
    setSearchTerm("");
    window.scrollTo({ top: 0 });
  };

  const handleRemovePlayStyle = useCallback(
    (id: number, plus: boolean) => {
      dispatch(applyPlayStyle({ id, plus }));
    },
    [dispatch],
  );

  const handleApplyPlayStyle = useCallback(
    (playStyle: PlayStylesLabRecommendation) => {
      dispatch(
        applyPlayStyle({
          id:
            playStyle.playStylesPlus.length > 0
              ? playStyle.playStylesPlus[0]
              : playStyle.playStyles[0],
          plus: playStyle.playStylesPlus.length > 0,
        }),
      );
      setSearchTerm("");
    },
    [dispatch],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(
        saveEvolution({
          resourceId: Number(resourceId),
          evoPathId: currentPlayStylesLabPlayer?.evoPathId,
          labPlayStyles: labPlayStyles,
          labPlayStylesPlus: labPlayStylesPlus,
        }),
      ).unwrap();
      dispatch(
        addAlert({
          type: "success",
          description: t("detailed.alerts.saved"),
          persistent: true,
          link: {
            label: t("detailed.alerts.viewSaved"),
            to: "/evolutions/my-evolutions/saved",
          },
        }),
      );
      // save (evo_player) is fired server-side in peareasy-services
      // (SavedEvolutionPlayersController) when this POST hits the backend.
    } catch {
      dispatch(
        addAlert({
          type: "error",
          description: t("detailed.alerts.saveFailed"),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  // Save is one click: nothing applied is a warning, logged out parks the
  // save until sign-in completes.
  const handleSaveClicked = () => {
    if (!canSave) {
      dispatch(
        addAlert({
          type: "warning",
          description: t("detailed.alerts.nothingToSave"),
        }),
      );
      return;
    }
    if (!isLoggedIn) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "save-evo",
        }),
      );
      setAwaitingLogin("save");
      return;
    }
    handleSave();
  };

  useEffect(() => {
    if (awaitingLogin && isLoggedIn) {
      handleSave();
      setAwaitingLogin(undefined);
    }
  }, [isLoggedIn, awaitingLogin]);

  // Real names once known; the URL slugs only fill in before the player loads.
  const playerLabel = useMemo(() => {
    if (!name) return "";
    return [
      currentPlayStylesLabPlayer?.name || capitalizeWordsAndRemoveDashes(name),
      headingVersionName(
        versionNameBySlug(versionAssets, version) ??
          capitalizeWordsAndRemoveDashes(version),
      ),
    ]
      .filter(Boolean)
      .join(" ");
  }, [name, version, currentPlayStylesLabPlayer?.name, versionAssets]);

  const heroCopy = useMemo(
    () => ({
      helmetTitle: t("playStylesLab.picker.helmetTitle", { game: GAME_NAME }),
      pageTitle: undefined,
      helmetDescription: t("playStylesLab.picker.helmetDescription", {
        game: GAME_NAME,
      }),
      subtitle: t("playStylesLab.picker.subtitle"),
      helmetCanonicalLink: rootCanonicalUrl("/playstyles-lab"),
    }),
    [t],
  );

  if (resourceId && notFoundResourceId === resourceId) {
    return <PlayerNotFound />;
  }

  const navigateToPlayerDetails = () => {
    const playerToNavigate = currentPlayStylesLabPlayer;
    if (!playerToNavigate) return;

    const navLink = generateNavigationUrl({
      name: playerToNavigate.name,
      resourceId: playerToNavigate.resourceId,
      playerRoleId: playerToNavigate.playerRoleId,
      evoPathId: playerToNavigate.evoPathId,
      basePath: "/playstyles-lab/details",
      labPlayStylesPlus: labPlayStylesPlus,
      labPlayStyles: labPlayStyles,
    });

    navigate(navLink, {
      state: {
        p: playerToNavigate,
      },
    });
  };

  return (
    <main className="min-h-screen mx-auto bg-new-gray-850">
      <HeroSection
        helmetTitle={heroCopy.helmetTitle}
        pageTitle={heroCopy.pageTitle}
        helmetDescription={heroCopy.helmetDescription}
        subtitle={heroCopy.subtitle}
        helmetCanonicalLink={heroCopy.helmetCanonicalLink}
        back={goBack}
        alignCenter={false}
        backgroundImageUrl={
          import.meta.env.VITE_APP_ASSETS_BASE_URL +
          `/${PINNED_ASSET_SLUG}/page-headers/evo-header.png`
        }
      >
        <div ref={refTopBar} />
        {basePlayer && (
          <CardBuilderTopSection
            scrollPastTopBar={scrollPastTopBar}
            basePlayer={basePlayer}
            currentEvolutionPlayer={currentPlayStylesLabPlayer}
            isEvolutionPlayer={false}
            handlePlayerClicked={navigateToPlayerDetails}
            currentStepData={null}
            upgrades={upgrades}
            handleClearPlaystyles={handleClearPlaystyles}
            variant="playstyles-lab"
            onRemovePlayStyle={handleRemovePlayStyle}
            removeDisabled={status === "pending"}
          />
        )}
      </HeroSection>
      <PlayersToolbar
        hasFilters={false}
        searchTerm={searchTerm}
        saving={saving}
        setSearchTerm={setSearchTerm}
        handleToolbarAction={handleToolbarAction}
        isMobile={isMobile}
        pageType="playstyles-lab"
        playerRole={selectedPlayerRole}
      />
      <div className="max-w-content mx-auto">
        <section className="flex flex-col gap-y-6 px-2 large:px-16 touch-device:px-4 mt-6">
          {basePlayer && (
            <>
              {searchTerm.length === 0 && (
                <PlayStylesRecommendations
                  recommendations={recommendations}
                  player={currentPlayStylesLabPlayer}
                  onApplyRecommendation={handleApplyRecommendation}
                  loading={status === "pending"}
                  onSave={handleSaveClicked}
                  saving={saving}
                  onShare={handleDownloadAssets}
                  onClear={handleClearPlaystyles}
                />
              )}
              <PlayStylesLabSteps
                playStylesPlus={playStylesPlus}
                playStyles={playStyles}
                labPlayStyles={labPlayStyles}
                labPlayStylesPlus={labPlayStylesPlus}
                basePlayStyles={basePlayer.playStyles ?? []}
                basePlayStylesPlus={basePlayer.playStylesPlus ?? []}
                searchTerm={searchTerm}
                onClickPlayStyle={handleApplyPlayStyle}
              />
            </>
          )}
        </section>
        {elementOpened === "playerRole" && basePlayer && (
          <EvolutionsStickySidebar
            onFiltersUpdated={() => {}}
            elementOpened={elementOpened}
            handleToolbarAction={handleToolbarAction}
            isTouchDevice={isTouchDevice}
            playerRoles={playerRoles}
            playerRole={selectedPlayerRole}
            handleSelectPlayerRole={handleSelectPlayerRole}
            refPlayerRole={refPlayerRole}
            hideRolesPlayerCannotPlay={basePlayer.possiblePositions}
          />
        )}
        {/* Floating counterpart to the compare pill (bottom-left): send the
            build back to the squad slot it was opened for. */}
        {comingFromSquadBuilder && (
          <FloatingRailSlot side="right" className="bottom-[var(--ad-gutter)]">
            <DesignSystemButton
              variant="primary"
              size="medium"
              onClick={sendToSquadBuilder}
            >
              {t("playStylesLab.sendToSquad")}
            </DesignSystemButton>
          </FloatingRailSlot>
        )}
      </div>
    </main>
  );
};
